import { Injectable, inject } from '@angular/core';
import {
    LogicStepDefinition,
    ProcessLogicStep,
    LogicStepParameterType,
    LogicStepParameter,
    WorkflowConfiguration,
} from '@wdx/shared/utils';
import {
    WorkflowNode,
    NodeAddedEvent,
    WorkflowNodeChildren,
    WorkflowNodeType,
} from '../models';
import { v4 as uuidv4 } from 'uuid';
import {
    Observable,
    catchError,
    filter,
    forkJoin,
    map,
    of,
    switchMap,
} from 'rxjs';
import { LogicstepCodeParametersCachedApiService } from './logicstep-code-parameters-cached-api.service';

@Injectable({ providedIn: 'root' })
export class WorkflowNodeHelperService {
    private readonly logicStepParameterCachedApiService = inject(
        LogicstepCodeParametersCachedApiService,
    );
    public stepDefinitions: LogicStepDefinition[];

    public updateNodeRecursively(
        nodes: WorkflowNode[],
        targetId: string,
        updatedNode: WorkflowNode,
    ): WorkflowNode[] {
        return this.modifyNodeRecursively(nodes, targetId, () => updatedNode);
    }

    public removeNodeRecursively(
        nodes: WorkflowNode[],
        targetId: string,
    ): WorkflowNode[] {
        return this.modifyNodeRecursively(nodes, targetId, () => null);
    }

    public createNode(
        steps: WorkflowNode[],
        selectedNode: WorkflowNode,
        nodeAdded: NodeAddedEvent,
    ): WorkflowNode[] {
        if (!selectedNode || !nodeAdded?.parentId) {
            return steps;
        }

        if (nodeAdded.parentId === 'root') {
            return [...steps, selectedNode];
        }

        const parentNode = steps.find(
            (s) => s.payload.Id === nodeAdded.parentId,
        );

        if (parentNode) {
            if (nodeAdded.logicCode) {
                parentNode.children[nodeAdded.logicCode].push(selectedNode);
            } else {
                const parentIndex = steps.indexOf(parentNode);
                steps.splice(parentIndex + 1, 0, selectedNode);
            }
            return steps;
        }

        steps.some((node) =>
            this.tryInsertNextToChild(node, nodeAdded, selectedNode),
        );

        return steps;
    }

    public convertStepToNode(
        logicStep: ProcessLogicStep,
    ): Observable<WorkflowNode> {
        const stepDef = this.stepDefinitions.find(
            (definition) => definition.code === logicStep?.Code,
        );

        if (!stepDef?.code) {
            return of(null);
        }

        return this.logicStepParameterCachedApiService
            .get(stepDef.code)
            .pipe(
                switchMap((params) => {
                    const baseNode = this.buildNode(
                        logicStep.Id,
                        logicStep,
                        stepDef,
                        params,
                    );
                    const { childrenMap, childObservables } =
                        this.buildChildrenFromStep(
                            logicStep,
                            baseNode.childLogicCodesOrder,
                        );
                    const resultNode: WorkflowNode = {
                        ...baseNode,
                        children: childrenMap,
                    };

                    return childObservables.length > 0
                        ? forkJoin(childObservables).pipe(map(() => resultNode))
                        : of(resultNode);
                }),
            );
    }

    public getWorkflowOutput(nodes: WorkflowNode[]): WorkflowConfiguration {
        const logicSteps = nodes
            .filter((n) => n.nodeType === WorkflowNodeType.LogicStep)
            .map((n) => {
                const serialized = this.serializeNode(n);

                if (
                    serialized.Code === 'If' ||
                    serialized.Code === 'MultipleIf'
                ) {
                    const prefixRegarding = (value: string) =>
                        value && !value.startsWith('regarding.')
                            ? `regarding.${value}`
                            : value;

                    if (
                        typeof serialized.Input === 'string' &&
                        serialized.Input.trim()
                    ) {
                        serialized.Input = prefixRegarding(serialized.Input);
                    }

                    if (Array.isArray(serialized.Conditions)) {
                        serialized.Conditions = serialized.Conditions.map(
                            (condition) => ({
                                ...condition,
                                Input: prefixRegarding(
                                    condition.Input as string,
                                ),
                            }),
                        );
                    }
                }

                return serialized;
            });

        const queryNode = nodes.find(
            (n) => n.nodeType === WorkflowNodeType.Query,
        );

        const query = queryNode?.payload?.query;
        const queryDescription = queryNode?.payload?.queryDescription ?? '';
        const queryEntity = queryNode?.targetEntityType;
        const exclusionPeriod =
            queryNode?.payload?.exclusionPeriod ??
            queryNode?.payload?.exclutionPeriod;

        return {
            ...(query ? { query } : {}),
            ...(queryDescription ? { queryDescription } : null),
            ...(queryEntity ? { queryEntity } : null),
            ...(exclusionPeriod !== undefined && exclusionPeriod !== null
                ? { exclusionPeriod }
                : null),
            logicSteps: logicSteps,
        };
    }

    public buildActiveNodeId(nodeId: string, logicCode?: string) {
        return logicCode ? `${nodeId}-${logicCode}` : nodeId;
    }

    public buildNode(
        nodeId: string | null,
        logicStep: ProcessLogicStep,
        stepDef: LogicStepDefinition,
        params: LogicStepParameter[],
    ): WorkflowNode {
        const isValid = !params
            .filter((p) => p.isRequired)
            .some((p) =>
                p.isArray
                    ? !(logicStep[p.code]?.length > 0)
                    : !logicStep[p.code],
            );
        const logicCodes = this.getLogicCodesFromParams(params);

        return {
            ...stepDef,
            nodeType: WorkflowNodeType.LogicStep,
            payload: {
                ...logicStep,
                Code: logicStep.Code ?? stepDef?.code ?? '',
                Id: nodeId ?? logicStep.Id ?? uuidv4(),
            },
            isValid: isValid,
            isRemovable: true,
            children: Object.fromEntries(logicCodes.map((code) => [code, []])),
            childLogicCodesOrder: logicCodes,
        };
    }

    private getLogicCodesFromParams(params: LogicStepParameter[]): string[] {
        return params
            .filter((p) => p.kind === LogicStepParameterType.LogicStep)
            .map((p) => p.code);
    }

    private buildChildrenFromStep(
        logicStep: ProcessLogicStep,
        logicCodes: string[],
    ): {
        childrenMap: WorkflowNodeChildren;
        childObservables: Observable<void>[];
    } {
        // basically here we building the children nodes for all available 'LogicStep' types
        // for this step
        const childrenMap: WorkflowNodeChildren = Object.fromEntries(
            logicCodes.map((logicCode) => [logicCode, []]),
        );
        const childObservables: Observable<void>[] = [];

        for (const logicCode of logicCodes) {
            const childSteps: ProcessLogicStep[] = logicStep[logicCode] ?? [];
            for (const childStep of childSteps) {
                const childObs = this.convertStepToNode(childStep).pipe(
                    filter((childNode): childNode is WorkflowNode => !!childNode),
                    map((childNode) => {
                        childNode.logicCode = logicCode;
                        childrenMap[logicCode].push(childNode);
                    }),
                );
                childObservables.push(childObs);
            }
        }
        return { childrenMap, childObservables };
    }

    private tryInsertNextToChild(
        node: WorkflowNode,
        nodeAdded: NodeAddedEvent,
        newNode: WorkflowNode,
    ): boolean {
        if (!node.children || Object.values(node.children).length === 0) {
            return false;
        }

        for (const logicCode in node.children) {
            const childrenArray = node.children[logicCode];

            const parentNode = childrenArray.find(
                (s) => s.payload.Id === nodeAdded.parentId,
            );

            if (parentNode) {
                if (nodeAdded.isNewLogic) {
                    parentNode.children[nodeAdded.logicCode].push(newNode);
                } else {
                    const parentIndex = childrenArray.indexOf(parentNode);
                    childrenArray.splice(parentIndex + 1, 0, newNode);
                }
                return true;
            }

            for (const child of childrenArray) {
                if (this.tryInsertNextToChild(child, nodeAdded, newNode)) {
                    return true;
                }
            }
        }

        return false;
    }

    private modifyNodeRecursively(
        nodes: WorkflowNode[],
        targetId: string,
        modify: (node: WorkflowNode) => WorkflowNode | null,
    ): WorkflowNode[] {
        return nodes
            .map((node) => {
                if (node.payload.Id === targetId) {
                    return modify(node);
                }

                if (node.children) {
                    const updatedChildren: WorkflowNodeChildren = {};
                    for (const logicCode in node.children) {
                        updatedChildren[logicCode] = this.modifyNodeRecursively(
                            node.children[logicCode],
                            targetId,
                            modify,
                        );
                    }
                    return { ...node, children: updatedChildren };
                }

                return node;
            })
            .filter((n): n is WorkflowNode => n !== null);
    }

    private serializeNode(node: WorkflowNode): any {
        const basePayload = { ...node.payload };

        if (node.children && node.childLogicCodesOrder) {
            for (const logicCode of node.childLogicCodesOrder) {
                const children = node.children[logicCode] ?? [];
                basePayload[logicCode] = children.map((child) =>
                    this.serializeNode(child),
                );
            }
        }

        return basePayload;
    }
}
